import {memo, useRef} from 'react'
import {clsx} from 'clsx'
import styles from './PullRequestDiffsAnimation.module.css'
import {usePrefersReducedMotion} from '@github-ui/use-prefers-reduced-motion'

import firstFrameImage from './images/mona-hifive-first-frame.png'
import reducedMotionImage from './images/mona-hifive-reduced-motion.png'
import animatedImage from './images/mona-hifive-loop-once.gif'

/*
 * Whimsy
 * Show a fun image at the end of the diff list if there's enough visible entries
 */
const PullRequestDiffsAnimationComponent = () => {
  const prefersReducedMotion = usePrefersReducedMotion()

  const animatedImageRef = useRef<HTMLImageElement>(null)
  const observerRef = useRef<IntersectionObserver | null>(null)

  const setupIntersectionObserver = (element: HTMLImageElement) => {
    if (prefersReducedMotion) {
      return
    }

    let timeoutId: ReturnType<typeof setTimeout> | undefined

    const observerCallback: IntersectionObserverCallback = (entries, observer) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          // Add a lil delay so the animation is more noticeable
          timeoutId = setTimeout(() => {
            // When the image enters the viewport, hide it and reveal the animation
            ;(entry.target as HTMLElement).style.display = 'none'
            if (animatedImageRef.current) {
              animatedImageRef.current.style.opacity = '1'
            }
            // Stop observing after it has appeared so it doesn't reanimate every time we scroll down
            observer.unobserve(entry.target)
          }, 1000)
        }
      }
    }

    const observer = new IntersectionObserver(observerCallback, {
      // Trigger when 100% of the first frame image is visible
      threshold: 1,
    })

    observerRef.current = observer
    observer.observe(element)

    // Return cleanup function
    return () => {
      observer.disconnect()
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }

  const firstFrameRef = (element: HTMLImageElement | null) => {
    if (element) {
      setupIntersectionObserver(element)
    } else {
      // Cleanup when element is unmounted
      if (observerRef.current) {
        observerRef.current.disconnect()
        observerRef.current = null
      }
    }
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.imageContainer}>
        <img
          src={animatedImage}
          alt="GIF of two octocats high fiving each other"
          className={clsx(styles.image, styles.animatedImage)}
          ref={animatedImageRef}
        />
        <img
          src={firstFrameImage}
          alt="Two octocats"
          className={clsx(styles.image, styles.firstFrameImage)}
          ref={firstFrameRef}
        />
        <img
          src={reducedMotionImage}
          alt="Two octocats high fiving each other"
          className={clsx(styles.image, styles.reducedMotionImage)}
        />
      </div>
      <span className="fgColor-muted">You made it to the end!</span>
    </div>
  )
}

export const PullRequestDiffsAnimation = memo(PullRequestDiffsAnimationComponent)
